<template>
  <CoreDrawer
    :value="value"
    :is-back="isBack"
    :title="t('Дата и время', options)"
    attach=".vue-the-doctors-filters .v-application"
    @input="closeModal"
    @back="closeModal"
  >
    <template #append-header>
      <v-btn
        v-if="isVisibleReset"
        color="primary"
        small
        text
        depressed
        class="ml-auto"
        @click="clickResetBtn"
      >
        {{ t('Сбросить', options) }}
      </v-btn>
    </template>
    <template #default>
      <div :class="isMobile ? 'px-2 py-6' : 'pa-6'">
        <!-- в weeks-num передаём 2 недели, а не 4, из-за технических ограничений в МФ,
        они не могут дать нам ответ для 4-х недель -->
        <CustomDatepicker
          :value="selectedDate"
          interval
          :weeks-num="2"
          class="mb-4"
          @change="handlePickDate"
        />
        <div class="d-flex justify-center mb-4">
          <v-chip
            color="bg-gray-40"
            text-color="text"
            class="mx-1"
            @click="handleClickChip('today')"
          >
            {{ t('Сегодня', options) }}
          </v-chip>
          <v-chip
            color="bg-gray-40"
            text-color="text"
            class="mx-1"
            @click="handleClickChip('tomorrow')"
          >
            {{ t('Завтра', options) }}
          </v-chip>
          <v-chip
            v-if="!WEEK_DATES.isTodaySaturday"
            color="bg-gray-40"
            text-color="text"
            class="mx-1"
            @click="handleClickChip('weekend')"
          >
            {{ t('На выходных', options) }}
          </v-chip>
        </div>
        <div
          :class="isMobile ? 'mb-4' : 'mb-6'"
          class="d-flex justify-center text-body-2 text-info--text"
        >
          {{ t('Выберите дату или диапазон', options) }}
        </div>
        <v-divider :class="isMobile ? 'mb-4' : 'mb-6'" />
        <div
          v-if="!isMobile"
          class="d-flex justify-center text-subtitle-1 text--text mb-7"
        >
          {{ t('Время посещения', options) }}
        </div>
        <CheckboxTime
          v-for="(checkbox, i) in dayTimesCheckboxes"
          :key="i"
          :value="selectedTime"
          multiple
          hide-details
          class="mt-0 mb-6"
          :class="{ 'pl-2': !isMobile }"
          :selected-value="checkbox.value"
          @input="handlePickTime"
        >
          <div class="text-body-1 text--text ml-4">
            {{ t(checkbox.name, options) }}
            <div class="text-secondary--text mt-1">
              {{ checkbox.time }}
            </div>
          </div>
        </CheckboxTime>
      </div>
    </template>
    <template #footer>
      <slot name="footer">
        <BtnShowDoctors
          :active="value"
          class="pa-2 elevation-8"
          @click:close="closeModal"
          @click:submit="handleClickSubmit"
        />
      </slot>
    </template>
  </CoreDrawer>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { toRefs, computed } from 'vue'
import I18NextVue from 'i18next'
import CustomDatepicker from 'www/vue_components/common/core/CustomDatepicker.vue'
import CoreDrawer from 'src/core/CoreDrawer/CoreDrawer.vue'
import CheckboxTime from 'www/vue_components/common/TheFilters/doctors/components/DateFilters/components/CheckboxTime.vue'
import BtnShowDoctors from 'www/vue_components/common/TheFilters/doctors/components/BtnShowDoctors/BtnShowDoctors.vue'
import useFiltersStore from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore'
import { DAY_TIMES, WEEK_DATES } from 'www/vue_components/common/TheFilters/doctors/constants'
import { Filters } from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore.types'

const emit = defineEmits(['input', 'click:submit'])

const props = defineProps<{
  value: boolean
  isBack?: boolean
}>()
const { value } = toRefs(props)

const filtersStore = useFiltersStore()
const isMobile = window.MOBILE_VERSION
const dayTimesCheckboxes = Object.entries(DAY_TIMES).map(([value, { name, time }]) => ({ value, name, time }))

const selectedDate = computed(() => filtersStore.unsavedFilters.dateRange)
const selectedTime = computed(() => filtersStore.unsavedFilters.timesOfDay)
const isVisibleReset = computed(() => selectedDate.value || selectedTime.value?.length)

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

function clickResetBtn() {
  filtersStore.updateUnsavedFilters({
    dateRange: undefined,
    timesOfDay: undefined,
  })
}

function closeModal() {
  emit('input', false)
}

function handleClickSubmit() {
  const [startDate, endDate] = selectedDate.value || []
  const newDateRange: Filters['dateRange'] = startDate ? [startDate, endDate || startDate] : undefined
  const newTimesOfDay: Filters['timesOfDay'] = selectedTime.value

  filtersStore.updateUnsavedFilters({
    dateRange: newDateRange,
    timesOfDay: newTimesOfDay,
  })

  emit('click:submit')
}

function handlePickDate(pickedDate: Filters['dateRange']) {
  filtersStore.updateUnsavedFilters({ dateRange: pickedDate })
}

function handlePickTime(pickedTime: Filters['timesOfDay']) {
  if (!pickedTime?.length) {
    filtersStore.updateUnsavedFilters({ timesOfDay: undefined })

    return
  }

  const order = Object.keys(DAY_TIMES) // Утро должно идти раньше чем День или Вечер
  const pickedTimeSorted = pickedTime.sort((a, b) => order.indexOf(a) - order.indexOf(b))

  filtersStore.updateUnsavedFilters({ timesOfDay: pickedTimeSorted })
}

function handleClickChip(type: 'today' | 'tomorrow' | 'weekend') {
  if (type === 'today') {
    const today = format(WEEK_DATES.today, 'yyyy-MM-dd')

    filtersStore.updateUnsavedFilters({ dateRange: [today, today] })

    return
  }

  if (type === 'tomorrow') {
    const tomorrow = format(WEEK_DATES.tomorrow, 'yyyy-MM-dd')

    filtersStore.updateUnsavedFilters({ dateRange: [tomorrow, tomorrow] })

    return
  }

  const saturday = format(WEEK_DATES.saturday, 'yyyy-MM-dd')
  const sunday = format(WEEK_DATES.sunday, 'yyyy-MM-dd')

  filtersStore.updateUnsavedFilters({ dateRange: [saturday, sunday] })
}
</script>
